<template>
  <div v-if="!hideForRoutes.includes(route)" class="dialer-wrapper">
    <div class="dialer-icon" @click="toggleDialerDropdown">
      <Icon icon="mdi:phone" width="18" height="18" />
    </div>

    <div
      v-if="showDialerDropdown"
      class="dialer-dropdown"
      :class="{ 'dialer-dropdown--fixed': hasBeenDragged, 'dialer-dropdown--dragging': isDragging }"
      :style="panelStyle"
      @click.stop
    >
      <div class="dialer-header" @mousedown="startDrag">
        <div class="dialer-header__drag-hint" title="Drag to move">
          <Icon icon="mdi:drag" width="18" height="18" />
        </div>
        <button
          class="dialer-header__close"
          aria-label="Close"
          @mousedown.stop
          @click="closeDialer"
        >
          <Icon icon="mdi:close" width="18" height="18" />
        </button>
      </div>
      <div class="dialer-container">
        <div v-if="!isCallActive" class="dialer-input-screen">
          <!-- ... (Input screen remains) -->
          <div class="input-group">
            <span v-if="detectedCountryFlag" class="input-flag">{{ detectedCountryFlag }}</span>
            <span v-else class="input-icon">
              <Icon icon="charm:person" width="16" height="16" />
            </span>
            <div class="input-wrapper">
              <input
                v-model="searchTerm"
                @input="onDialerInput"
                @keyup.enter="startCall"
                type="text"
                class="dialer-input-field"
                placeholder="Search user or enter a number"
                autocomplete="off"
              />
            </div>
            <span class="input-icon">
              <Icon icon="mdi:phone-dial" width="16" height="16" />
            </span>
          </div>

          <div
            v-if="contacts && contacts.length && searchTerm && showSearchDropdown"
            class="contacts-dropdown"
          >
            <ul>
              <li v-for="contact in contacts" :key="contact.id" class="contact-item">
                <div class="contact-info" @click="selectContact(contact)">
                  <span class="contact-name-text">
                    {{ $filters.capitalize(contact.fname) }}
                    {{ $filters.capitalize(contact.lname) }}
                  </span>
                  <span v-if="contact.company?.name" class="contact-company">
                    {{ contact.company.name }}
                  </span>
                </div>
                <div v-if="contact.phones && contact.phones.length" class="contact-phones">
                  <div
                    v-for="(phone, idx) in contact.phones"
                    :key="idx"
                    class="contact-phone-row"
                    @click="selectContactPhone(contact, phone)"
                  >
                    <span class="phone-type-label">{{ phone.type || "PHONE" }}</span>
                    <span class="phone-number-text">{{ formatDisplayNumber(phone.number) }}</span>
                  </div>
                </div>
                <div v-else class="contact-no-phone">No phone</div>
              </li>
            </ul>
          </div>

          <div v-if="contactsIsLoading" class="loading-indicator">
            <Icon icon="mdi:loading" width="16" height="16" class="spinning" />
          </div>

          <div v-if="isPhoneInput && searchTerm" class="number-display">
            {{ formattedDisplayNumber }}
          </div>

          <div class="keypad">
            <button
              v-for="num in ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']"
              :key="num"
              class="keypad-btn"
              :class="{ 'zero-btn': num === '0' }"
              @click="num === '0' ? null : appendToSearchTerm(num)"
              @mousedown.prevent="num === '0' ? onKeyDown('0') : null"
              @touchstart.prevent="num === '0' ? onKeyDown('0') : null"
              @mouseup.prevent="num === '0' ? onKeyUp('0') : null"
              @touchend.prevent="num === '0' ? onKeyUp('0') : null"
            >
              <span v-if="num === '0'" class="zero-content">
                <span class="zero-number">0</span>
                <small class="zero-plus">+</small>
              </span>
              <span v-else>{{ num }}</span>
            </button>
          </div>

          <div class="dialer-actions">
            <button
              v-tooltip="{
                content: USAGE_BLOCKED_TOOLTIP,
                disabled: !isUsageBlocked || !searchTerm,
              }"
              @click="startCall"
              class="call-action-btn"
              :disabled="!searchTerm || isUsageBlocked"
            >
              <Icon icon="mdi:phone" width="18" height="18" />
            </button>
            <button @click="handleBackspace" class="backspace-btn" :disabled="!searchTerm">
              <Icon icon="mdi:backspace" width="18" height="18" />
            </button>
          </div>

          <!-- Calling From -->
          <div class="calling-from-section">
            <h6 class="calling-from-label">Calling from</h6>
            <div v-if="!hasPhoneNumbers" class="no-phone-number-banner">
              <Icon icon="mdi:information-outline" class="banner-info-icon" />
              <span class="banner-text">No phone number available.</span>
              <button class="banner-link-button" @click="redirectToBuyPhoneNumber">
                Add phone number
                <Icon icon="mdi:arrow-right" class="banner-arrow-icon" />
              </button>
            </div>
            <div v-else class="phone-number-select-wrapper">
              <div v-if="phoneNumbers.length > 1" class="phone-number-select">
                <Select
                  v-model="selectedNumberObj"
                  :options="phoneNumbers"
                  placeholder="Select a phone number"
                  label-key="number"
                  value-key="number"
                  :show-actions="false"
                  :show-add-button="false"
                  :formatter="formatPhoneNumberWithCountry"
                  :return-object="true"
                  @change="onPhoneNumberChange"
                />
              </div>
              <div v-else class="phone-number-display">
                <span
                  v-if="selectedNumberObj"
                  v-html="formatPhoneNumberWithCountry(selectedNumberObj)"
                ></span>
                <span
                  v-else-if="phoneNumbers.length === 1"
                  v-html="formatPhoneNumberWithCountry(phoneNumbers[0])"
                ></span>
              </div>
              <div class="add-phone-btn-wrapper" style="position: relative">
                <button
                  type="button"
                  class="add-option-btn-side"
                  @click="showBuyPhoneNumberModal = true"
                >
                  <Icon icon="mynaui:plus-solid" style="font-size: 16px" />
                </button>
                <div v-if="showBuyPhoneNumberModal" class="buy-phone-card">
                  <div class="card-header">
                    <div class="header-content">
                      <div class="icon">
                        <Icon
                          icon="mdi:phone-plus"
                          style="font-size: 16px; color: #3e3efe; font-weight: bold"
                        />
                      </div>
                      <span class="card-title" style="font-weight: bold">Buy Phone Number</span>
                    </div>
                    <button
                      type="button"
                      class="btn-close-card"
                      @click="showBuyPhoneNumberModal = false"
                    >
                      <Icon icon="mdi:close" style="font-size: 14px" />
                    </button>
                  </div>
                  <div class="card-body">
                    <p class="card-text">
                      Purchase an additional phone number for your organization.
                    </p>
                    <Button
                      variant="primary"
                      size="md"
                      icon="mdi:phone-plus"
                      icon-position="left"
                      @click="redirectToBuyPhoneNumber"
                      block
                    >
                      Buy Phone Number
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End dialer-dropdown -->
    </div>

    <CallSessionModal
      :icps="icps"
      :show="
        showAIAssistantPrompt ||
        isPreparingAIContext ||
        ['connecting', 'ringing', 'in-call', 'reconnecting'].includes(status) ||
        !!incomingCallData
      "
      :contactName="
        selectedContact
          ? (
              $filters.capitalize(selectedContact.fname) +
              ' ' +
              $filters.capitalize(selectedContact.lname)
            ).trim()
          : undefined
      "
      :to="to"
      :status="status"
      :isPreparingAIContext="isPreparingAIContext"
      :showAIPrompt="showAIAssistantPrompt"
      :incomingCallData="incomingCallData"
      :formattedIncomingCallTimer="'00:00:00'"
      :formattedCallTimer="formattedCallTimer"
      :isMuted="isMuted"
      :saveRecording="saveRecording"
      :enableTranscription="enableTranscription"
      :parentCallSid="parentCallSid"
      :currentCallSid="currentCallSid"
      :account="account"
      :microphones="microphones"
      :speakers="speakers"
      :selectedMicId="selectedMicId"
      :selectedSpeakerId="selectedSpeakerId"
      :isThinking="isThinking"
      :isStreaming="isStreaming"
      :hasSuggestions="hasSuggestions"
      :currentStreamingText="currentStreamingText"
      :latestSuggestion="latestSuggestion"
      :aiSuggestions="suggestions"
      @close="handleCloseCallModal"
      @proceed-with-ai="proceedWithAI"
      @proceed-without-ai="proceedWithoutAI"
      @answer-incoming-call="handleAnswerIncomingCall"
      @decline-incoming-call="handleDeclineIncomingCall"
      @hangup-incoming-call="handleHangupIncomingCall"
      @hangup="endCall"
      @toggle-record="toggleRecording"
      @toggle-mute="toggleMute"
      @transfer-call="handleTransfer"
      @select-microphone="selectMicrophone"
      @select-speaker="selectSpeaker"
      @send-digit="appendToIVRInputValue"
    />
  </div>
  <!-- End dialer-wrapper -->
</template>

<script>
import { markRaw } from "vue";
import debounce from "lodash/debounce";
import { Icon } from "@/components/ui";
import { Button, Select } from "@/components/ui";
import CallSessionModal from "@/components/call/CallSessionModal.vue";
import { GET_CONTACTS, GET_PHONE_NUMBERS } from "@/store/action-types";
import { formatLocation } from "@/utils/const";
import parsePhoneNumberFromString, { AsYouType } from "libphonenumber-js";
import Twilio from "@/services/twilio/twilio";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { useIncomingCall } from "@/composables/calls/useIncomingCall";
import { useAIAssistant } from "@/composables/calls/useAIAssistant";
import { useICPs } from "@/composables/icp/useICPs";

export default {
  name: "Dialer",
  components: {
    Icon,
    Button,
    Select,
    CallSessionModal,
  },
  setup() {
    const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();
    const { setOutboundCallSid, clearOutboundCallSid } = useIncomingCall();
    const {
      enableAssistant,
      prepareContext,
      disableAssistant,
      latestSuggestion,
      currentStreamingText,
      isThinking,
      isStreaming,
      hasSuggestions,
      suggestions,
    } = useAIAssistant();
    const { clearIncomingCall, setCallActive } = useIncomingCall();
    const { data: icpsData, isLoading: icpsLoading } = useICPs();

    return {
      showUsageBasedFeaturesModalOnce,
      isUsageBlocked,
      USAGE_BLOCKED_TOOLTIP,
      setOutboundCallSid,
      clearOutboundCallSid,
      enableAssistant,
      prepareContext,
      disableAssistant,
      latestSuggestion,
      currentStreamingText,
      isThinking,
      isStreaming,
      hasSuggestions,
      suggestions,
      clearIncomingCall,
      setCallActive,
      icpsData,
      icpsLoading,
    };
  },
  props: {
    route: {
      type: String,
      required: true,
    },
    account: {
      type: Object,
      required: true,
    },
    hideForRoutes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showDialerDropdown: false,
      showIVRDropdown: false,
      showMicrophoneDropdown: false,
      showSearchDropdown: false,
      selectedContact: null,
      searchTerm: "",
      to: null,
      status: "ready",
      isMuted: false,
      saveRecording: true,
      callDuration: 0,
      callTimer: null,
      currentCall: null,
      currentCallSid: null,
      parentCallSid: null,
      microphones: [],
      speakers: [],
      selectedMicId: "",
      selectedSpeakerId: "",
      ivrInputValue: "",
      pressTimer: null,
      pressedKey: null,
      currentPage: 1,
      perPage: 10,
      selectedNumber: null,
      selectedNumberObj: null,
      showBuyPhoneNumberModal: false,
      audioContext: null,
      isDragging: false,
      hasBeenDragged: false,
      dragPosX: 0,
      dragPosY: 0,
      dragOffsetX: 0,
      dragOffsetY: 0,
      showAIAssistantPrompt: false,
      pendingCallWithAI: false,
      // ICP picked on the pre-call prompt (AI or manual) — sent with the call
      // so the AI call summary uses it.
      pendingCallIcpId: null,
      isPreparingAIContext: false,
      incomingCallData: null,
    };
  },
  computed: {
    panelStyle() {
      if (!this.hasBeenDragged) return {};
      return {
        left: `${this.dragPosX}px`,
        top: `${this.dragPosY}px`,
      };
    },
    contacts() {
      return this.$store.getters.contacts.data;
    },
    contactsIsLoading() {
      return this.$store.getters.contacts.isLoading;
    },
    isCallActive() {
      return ["ringing", "connecting", "in-call", "reconnecting"].includes(this.status);
    },
    isRecording() {
      return this.account.allowCallRecording && this.saveRecording && this.isCallActive;
    },
    formattedCallTimer() {
      const hours = String(Math.floor(this.callDuration / 3600)).padStart(2, "0");
      const minutes = String(Math.floor((this.callDuration % 3600) / 60)).padStart(2, "0");
      const seconds = String(this.callDuration % 60).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    phoneNumbers() {
      const storePhoneNumbers = this.$store.getters.phoneNumbers.data || [];
      return storePhoneNumbers;
    },
    hasPhoneNumbers() {
      return this.phoneNumbers && this.phoneNumbers.length > 0;
    },
    icps() {
      return this.icpsData || [];
    },
    isPhoneInput() {
      if (!this.searchTerm) return false;
      return /^[+\d(]/.test(this.searchTerm) && !/[a-zA-Z]/.test(this.searchTerm);
    },
    formattedDisplayNumber() {
      if (!this.searchTerm || !this.isPhoneInput) return "";
      return new AsYouType().input(this.searchTerm);
    },
    detectedCountryFlag() {
      if (!this.searchTerm || !this.isPhoneInput) return "";
      try {
        const phone = parsePhoneNumberFromString(this.searchTerm);
        if (phone && phone.country) {
          return phone.country
            .toUpperCase()
            .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)));
        }
      } catch {
        // ignore
      }
      return "";
    },
    enableTranscription() {
      return this.account?.enableTranscription !== false;
    },
    enableAISummary() {
      return this.enableTranscription && this.account?.enableAISummary !== false;
    },
  },
  methods: {
    toggleDialerDropdown() {
      this.showDialerDropdown = !this.showDialerDropdown;
      if (this.showDialerDropdown) {
        this.initializeTwilio();
        this.getPhoneNumbers();
        setTimeout(() => {
          document.addEventListener("click", this.handleClickOutside);
        }, 0);
      } else {
        this.hasBeenDragged = false;
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    closeDialer() {
      this.showDialerDropdown = false;
      this.hasBeenDragged = false;
      this.showAIAssistantPrompt = false;
      document.removeEventListener("click", this.handleClickOutside);
    },
    closeCallScreen() {
      this.showDialerDropdown = false;
      this.hasBeenDragged = false;
      if (this.currentCall) {
        this.endCall();
      }
    },
    handleClickOutside(event) {
      const dropdown = this.$el?.querySelector(".dialer-dropdown");
      const modal = document.querySelector(".call-modal"); // CallSessionModal's internal class
      if (
        dropdown &&
        !dropdown.contains(event.target) &&
        (!modal || !modal.contains(event.target))
      ) {
        this.closeDialer();
      }
    },
    async initializeTwilio() {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.error("❌ Microphone access denied:", err);
        this.$toast.error("Please allow microphone access");
        return;
      }

      const all = await navigator.mediaDevices.enumerateDevices();
      this.microphones = all
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({ deviceId: d.deviceId, label: d.label || "Microphone" }));
      if (this.microphones.length && !this.selectedMicId) {
        this.selectedMicId = this.microphones[0].deviceId;
      }

      const identity = `user_${this.account.id}`;
      await Twilio.initTwilio(identity).catch((err) => {
        console.error(err);
        this.status = "error";
      });

      // Populate speakers from the Twilio SDK once the device is ready.
      try {
        this.speakers = Twilio.getSpeakers();
        if (this.speakers.length && !this.selectedSpeakerId) {
          this.selectedSpeakerId = this.speakers[0].deviceId;
        }
      } catch (err) {
        console.warn("[Dialer] Failed to enumerate speakers:", err);
      }

      Twilio.onRinging(() => {
        this.status = "ringing";
      });
      Twilio.onConnect(() => {
        this.status = "in-call";
        this.startCallTimer();
      });
      Twilio.onDisconnect(() => {
        this.status = "ended";
        this.stopCallTimer();
      });
      Twilio.onError(() => {
        this.status = "error";
        this.stopCallTimer();
      });
    },
    startCallTimer() {
      this.callDuration = 0;
      if (this.callTimer) clearInterval(this.callTimer);
      this.callTimer = setInterval(() => {
        this.callDuration++;
      }, 1000);
    },
    stopCallTimer() {
      if (this.callTimer) {
        clearInterval(this.callTimer);
        this.callTimer = null;
      }
    },
    toggleMute() {
      if (!this.currentCall) return;
      this.isMuted = !this.isMuted;
      this.currentCall.mute(this.isMuted);
    },
    async toggleRecording() {
      if (!this.currentCall || !this.currentCallSid) {
        this.$toast.info("No ongoing call");
        return;
      }

      if (!this.account.allowCallRecording) {
        this.$toast.info("Please enable recording in settings.");
        return;
      }

      const wasRecording = this.saveRecording;
      this.saveRecording = !wasRecording; // optimistic
      const sid = this.parentCallSid || this.currentCallSid;
      try {
        if (wasRecording) {
          await Twilio.pauseRecording(sid);
          this.$toast.info("Recording paused");
        } else {
          await Twilio.resumeRecording(sid);
          this.$toast.info("Recording resumed");
        }
      } catch (err) {
        this.saveRecording = wasRecording; // rollback
        this.$toast.error("Failed to update recording");
        console.error("[Dialer] toggleRecording failed:", err);
      }
    },
    async selectMicrophone(deviceId) {
      try {
        await Twilio.setMicrophone(deviceId);
        this.selectedMicId = deviceId;
        this.$toast.info("Switched mic!");
      } catch (err) {
        this.$toast.error("Failed to switch microphone");
        console.error("[Dialer] selectMicrophone failed:", err);
      }
      this.showMicrophoneDropdown = false;
    },
    async selectSpeaker(deviceId) {
      try {
        await Twilio.setSpeaker(deviceId);
        this.selectedSpeakerId = deviceId;
        this.$toast.info("Switched speaker");
      } catch (err) {
        this.$toast.error("Failed to switch speaker");
        console.error("[Dialer] selectSpeaker failed:", err);
      }
    },
    appendToIVRInputValue(char) {
      this.playDTMF(char);
      this.ivrInputValue += char;
      Twilio.sendDigits(char);
    },
    onKeyDownIVR(key) {
      if (key !== "0") {
        this.appendToIVRInputValue(key);
        return;
      }
      this.pressedKey = key;
      this.pressTimer = setTimeout(() => {
        this.appendToIVRInputValue("+");
        this.pressedKey = null;
      }, 600);
    },
    onKeyUpIVR(key) {
      if (this.pressedKey === "0" && this.pressTimer) {
        clearTimeout(this.pressTimer);
        this.appendToIVRInputValue("0");
      }
      this.pressTimer = null;
      this.pressedKey = null;
    },
    async proceedWithAI(icpId) {
      this.pendingCallWithAI = true;
      this.pendingCallIcpId = icpId || null;
      this.isPreparingAIContext = true; // show spinner BEFORE hiding the choice prompt
      this.showAIAssistantPrompt = false; // hides buttons; spinner stays via isPreparingAIContext

      const contactId = this.selectedContact?.id;

      try {
        if (contactId) {
          await Promise.race([
            this.prepareContext(contactId, icpId),
            new Promise((resolve) => setTimeout(resolve, 25_000)),
          ]);
        }
      } catch (err) {
        console.warn("[Dialer] Pre-call context prep failed, proceeding anyway:", err);
      } finally {
        this.isPreparingAIContext = false;
      }

      this.executeCall();
    },
    proceedWithoutAI(icpId) {
      this.showAIAssistantPrompt = false;
      this.pendingCallWithAI = false;
      this.pendingCallIcpId = icpId || null;
      this.executeCall();
    },
    handleCloseCallModal() {
      this.closeDialer();
      if (this.currentCall) {
        this.endCall();
      }
    },
    async handleAnswerIncomingCall() {
      try {
        await Twilio.answerIncomingCall();
        this.setCallActive();
        this.status = "in-call";
        this.$toast.success("Call answered");
      } catch (err) {
        console.error("[Dialer] Failed to answer call:", err);
        this.$toast.error("Failed to answer call");
      }
    },
    async handleDeclineIncomingCall() {
      try {
        await Twilio.rejectIncomingCall();
        this.clearIncomingCall();
        this.$toast.info("Call declined");
      } catch (err) {
        console.error("[Dialer] Failed to reject call:", err);
      }
    },
    async handleHangupIncomingCall() {
      try {
        await Twilio.hangup();
        this.clearIncomingCall();
      } catch (err) {
        console.error("[Dialer] Failed to hangup call:", err);
      }
    },
    async handleTransfer(phoneNumber) {
      if (!this.currentCall || !this.currentCallSid) {
        this.$toast.info("No ongoing call to transfer");
        return;
      }

      const sid = this.parentCallSid || this.currentCallSid;
      try {
        await Twilio.transferCall(sid, phoneNumber);
        this.$toast.success("Transfer initiated");
        // The agent's leg drops once the child is redirected; end the local UI state.
        this.endCall();
      } catch (error) {
        console.error("[Dialer] Transfer failed:", error);
        this.$toast.error("Unable to transfer call");
      }
    },
    async startCall() {
      if (this.showAIAssistantPrompt) return;
      await this.showUsageBasedFeaturesModalOnce("call", async () => {
        if (!this.to) {
          this.$toast.info("Please enter a number.");
          return;
        }

        if (!this.hasPhoneNumbers) {
          this.$toast.error(
            "No phone number available. Please purchase a phone number to make calls."
          );
          this.showBuyPhoneNumberModal = true;
          return;
        }

        if (!this.selectedNumber || !this.selectedNumberObj) {
          this.$toast.error("Please select a phone number to make calls.");
          return;
        }

        await navigator.mediaDevices.getUserMedia({ audio: true });

        // If calling a known contact, show AI assistant prompt first
        if (this.selectedContact?.id) {
          this.to = this.$filters.formatPhoneNumber(this.to);
          this.showAIAssistantPrompt = true;
          return;
        }

        // No contact — call directly without AI
        this.pendingCallWithAI = false;
        this.pendingCallIcpId = null;
        this.to = this.$filters.formatPhoneNumber(this.to);
        this.executeCall();
      });
    },
    async executeCall() {
      this.status = "connecting";

      const result = await Twilio.makeCall(
        this.to,
        this.account.id,
        this.selectedContact?.id,
        null,
        this.account.organizationId,
        this.enableTranscription,
        this.enableAISummary,
        "",
        this.pendingCallIcpId || ""
      );

      // Close the dialer pad once the call starts
      this.showDialerDropdown = false;
      this.hasBeenDragged = false;

      const { call, callSid } = result;
      this.currentCall = markRaw(call);
      if (callSid) {
        this.currentCallSid = callSid;
        this.parentCallSid = callSid;
        this.setOutboundCallSid(callSid);
      } else {
        console.warn("[LiveCaption][Dialer] No callSid from makeCall result");
      }

      call.on("ringing", () => {
        this.status = "ringing";
      });

      call.on("accept", (conn) => {
        this.status = "in-call";
        this.currentCallSid = conn.parameters.CallSid;
        this.parentCallSid = conn.parameters.CallSid;
        this.setOutboundCallSid(conn.parameters.CallSid);
        this.startCallTimer();

        // Enable AI assistant now that we have the confirmed SID
        if (this.pendingCallWithAI && this.selectedContact?.id) {
          const confirmedSid = conn.parameters.CallSid;
          this.enableAssistant(confirmedSid, this.selectedContact.id, confirmedSid).catch((err) => {
            console.warn("[Dialer] AI assistant enable failed:", err);
          });
          this.pendingCallWithAI = false;
        }
      });

      call.on("disconnect", () => {
        this.status = "ended";
        this.$toast.info("Call Disconnected");
        this.stopCallTimer();
        this.clearOutboundCallSid();
        this.pendingCallWithAI = false;
        this.disableAssistant();
        if (this.showDialerDropdown) {
          setTimeout(() => {
            this.showIVRDropdown = false;
            this.closeDialer();
          }, 1500);
        }
      });

      call.on("reconnecting", () => {
        this.status = "reconnecting";
      });

      call.on("reconnected", () => {
        this.status = "in-call";
      });

      call.on("reject", () => {
        this.status = "rejected";
        this.stopCallTimer();
        this.clearOutboundCallSid();
        this.pendingCallWithAI = false;
        this.disableAssistant();
        this.$toast.info("Call Rejected");
        if (this.showDialerDropdown) {
          setTimeout(() => {
            this.showIVRDropdown = false;
            this.closeDialer();
          }, 1500);
        }
      });

      call.on("cancel", () => {
        this.status = "cancel";
        this.stopCallTimer();
        this.clearOutboundCallSid();
        this.pendingCallWithAI = false;
        this.disableAssistant();
        this.$toast.info("Call has been canceled");
        if (this.showDialerDropdown) {
          setTimeout(() => {
            this.showIVRDropdown = false;
            this.closeDialer();
          }, 1500);
        }
      });

      call.on("error", () => {
        this.status = "error";
        this.$toast.error("Could not make call.");
        this.stopCallTimer();
        this.clearOutboundCallSid();
        this.pendingCallWithAI = false;
        this.disableAssistant();
        if (this.showDialerDropdown) {
          setTimeout(() => {
            this.showIVRDropdown = false;
            this.closeDialer();
          }, 1500);
        }
      });
    },
    endCall() {
      if (this.currentCall) {
        this.currentCall.disconnect();
      }
      Twilio.hangup();
      this.status = "ready";
      this.currentCall = null;
      this.clearOutboundCallSid();
      this.pendingCallWithAI = false;
      this.disableAssistant();
      this.showIVRDropdown = false;
      this.closeDialer();
    },
    toggleIVRDropdown() {
      this.showIVRDropdown = !this.showIVRDropdown;
    },
    onDialerInput() {
      if (this.isPhoneInput) {
        const raw = this.stripFormatting(this.searchTerm);
        this.to = raw;
        this.searchTerm = raw ? new AsYouType().input(raw) : "";
      }
      this.debouncedSearch();
    },
    appendToSearchTerm(char) {
      this.playDTMF(char);
      const raw = this.stripFormatting(this.searchTerm) + char;
      this.to = raw;
      this.searchTerm = new AsYouType().input(raw);
      this.showSearchDropdown = false;
    },
    handleBackspace() {
      if (this.isPhoneInput) {
        const raw = this.stripFormatting(this.searchTerm).slice(0, -1);
        this.to = raw || null;
        this.searchTerm = raw ? new AsYouType().input(raw) : "";
      } else {
        this.searchTerm = this.searchTerm.slice(0, -1);
        this.to = this.searchTerm || null;
      }
    },
    stripFormatting(str) {
      return (str || "").replace(/[\s().\-/]/g, "");
    },
    formatDisplayNumber(number) {
      if (!number) return "";
      try {
        return new AsYouType().input(number);
      } catch {
        return number;
      }
    },
    selectContactPhone(contact, phone) {
      this.selectedContact = contact;
      this.to = phone.number;
      this.searchTerm = this.formatDisplayNumber(phone.number);
      this.showSearchDropdown = false;
    },
    playDTMF(key) {
      const dtmfFreqs = {
        1: [697, 1209],
        2: [697, 1336],
        3: [697, 1477],
        4: [770, 1209],
        5: [770, 1336],
        6: [770, 1477],
        7: [852, 1209],
        8: [852, 1336],
        9: [852, 1477],
        "*": [941, 1209],
        0: [941, 1336],
        "#": [941, 1477],
        "+": [941, 1336],
      };
      const freqs = dtmfFreqs[key];
      if (!freqs) return;
      try {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = this.audioContext;
        const gain = ctx.createGain();
        gain.gain.value = 0.1;
        gain.connect(ctx.destination);
        const now = ctx.currentTime;
        freqs.forEach((freq) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          osc.connect(gain);
          osc.start(now);
          osc.stop(now + 0.1);
        });
      } catch (e) {
        // no-op
      }
    },
    onKeyDown(key) {
      if (key !== "0") {
        this.appendToSearchTerm(key);
        return;
      }
      this.pressedKey = key;
      this.pressTimer = setTimeout(() => {
        this.appendToSearchTerm("+");
        this.pressedKey = null;
      }, 600);
    },
    onKeyUp(key) {
      if (this.pressedKey === "0" && this.pressTimer) {
        clearTimeout(this.pressTimer);
        this.appendToSearchTerm("0");
      }
      this.pressTimer = null;
      this.pressedKey = null;
    },
    selectContact(contact) {
      this.selectedContact = contact;
      const phone = contact.phones?.[0];
      const number = phone?.number || contact.mobileNumber || contact.directNumber || "";
      this.to = number;
      this.searchTerm = this.formatDisplayNumber(number);
      this.showSearchDropdown = false;
    },
    debouncedSearch: debounce(function () {
      if (!this.searchTerm || !this.searchTerm.trim()) {
        this.showSearchDropdown = false;
        this.to = null;
        return;
      }
      if (this.isPhoneInput) {
        this.to = this.stripFormatting(this.searchTerm);
      }
      this.showSearchDropdown = true;
      this.getContacts();
    }, 300),
    getContacts() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
        organizationId: this.account.organizationId,
      };

      if (this.isPhoneInput) {
        params.mobileNumber = this.stripFormatting(this.searchTerm);
      } else {
        params.searchTerm = this.searchTerm;
      }

      if (this.$route.query.owner && this.$route.query.owner != "All") {
        params.createdById = this.$route.query.owner;
      }

      this.$store.dispatch(GET_CONTACTS, params);
    },
    getPhoneNumbers() {
      this.$store
        .dispatch(GET_PHONE_NUMBERS, {
          organizationId: this.account.organizationId,
        })
        .then((res) => {
          if (res && this.phoneNumbers?.length > 0) {
            // Auto-select the first phone number
            this.selectedNumber = this.phoneNumbers[0]?.number;
            this.selectedNumberObj = this.phoneNumbers[0];
          } else {
            this.selectedNumber = null;
            this.selectedNumberObj = null;
          }
        });
    },
    onPhoneNumberChange(phoneNumber) {
      this.selectedNumberObj = phoneNumber;
      this.selectedNumber = phoneNumber?.number || phoneNumber;
    },
    formatPhoneNumberWithCountry(option) {
      if (!option || !option.number) return option?.number || "";

      try {
        const phoneNumber = parsePhoneNumberFromString(option.number);
        if (phoneNumber && phoneNumber.isValid()) {
          const formattedNumber = phoneNumber.formatInternational();

          let locationText = "";
          if (option.city || option.region || option.countryCode) {
            locationText = formatLocation(option.city, option.region, option.countryCode);
          } else {
            const countryCode = phoneNumber.country;
            if (countryCode) {
              locationText = formatLocation(null, null, countryCode);
            }
          }

          const phoneNumberStyle =
            "font-weight: 500; color: #1f2937; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; font-size: 14px;";
          const locationStyle =
            "display: inline-flex; align-items: center; gap: 4px; color: #6b7280; font-size: 11px; font-weight: 400; white-space: nowrap; flex-shrink: 0; margin-left: 10px; padding: 2px 8px; background: #f3f4f6; border-radius: 12px;";
          const iconStyle = "font-size: 12px; color: #9ca3af; flex-shrink: 0;";
          const containerStyle =
            "display: flex; align-items: center; width: 100%; gap: 8px; flex-wrap: wrap;";

          return `
            <span style="${containerStyle}">
              <span style="${phoneNumberStyle}">${formattedNumber}</span>
              ${
                locationText
                  ? `
                <span style="${locationStyle}">
                  <svg style="${iconStyle}" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  ${locationText}
                </span>
              `
                  : ""
              }
            </span>
          `.trim();
        }
      } catch (error) {
        console.warn("Error parsing phone number:", error);
      }

      return option.number;
    },
    startDrag(event) {
      this.isDragging = true;
      const dropdown = this.$el.querySelector(".dialer-dropdown");
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect();
        if (!this.hasBeenDragged) {
          this.dragPosX = rect.left;
          this.dragPosY = rect.top;
          this.hasBeenDragged = true;
        }
        this.dragOffsetX = event.clientX - this.dragPosX;
        this.dragOffsetY = event.clientY - this.dragPosY;
      }
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
      event.preventDefault();
    },
    onDrag(event) {
      if (!this.isDragging) return;
      this.dragPosX = Math.max(
        0,
        Math.min(event.clientX - this.dragOffsetX, window.innerWidth - 380)
      );
      this.dragPosY = Math.max(
        0,
        Math.min(event.clientY - this.dragOffsetY, window.innerHeight - 100)
      );
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },
    redirectToBuyPhoneNumber() {
      const buyNumberUrl = `/settings/call-phone-numbers/buy-number?owner=${this.account.id}`;
      this.$router.push(buyNumberUrl);
      this.showBuyPhoneNumberModal = false;
    },
  },
  mounted() {
    this.getPhoneNumbers();
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    this.stopCallTimer();
  },
};
</script>

<style scoped>
.dialer-wrapper {
  position: relative;
}

.dialer-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  color: #3e3efe;
  background-color: #f3f4f6;
  transition: all 0.2s ease;
}

.dialer-icon:hover {
  background-color: #ebeaff;
  color: #2d2dd8;
  transform: scale(1.05);
}

.dialer-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 380px;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  box-shadow:
    0px 10px 25px -5px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  overflow: hidden;
}

.dialer-dropdown--fixed {
  position: fixed;
  top: auto;
  right: auto;
  z-index: 1060;
}

.dialer-dropdown--dragging {
  user-select: none;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  transition: none;
}

.dialer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 16px 16px 0 0;
  cursor: grab;
  user-select: none;
}

.dialer-header:active {
  cursor: grabbing;
}

.dialer-header__drag-hint {
  display: flex;
  align-items: center;
  color: #9ca3af;
  transition: color 0.15s ease;
}

.dialer-header:hover .dialer-header__drag-hint {
  color: #6b7280;
}

.dialer-header__close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.15s ease;
}

.dialer-header__close:hover {
  background: #e5e7eb;
  color: #374151;
}

.dialer-container {
  position: relative;
  padding: 4px 16px 16px;
  background: #ffffff;
}

.close-dialer-button,
.close-call-button {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
  z-index: 10;
}

.close-dialer-button:hover,
.close-call-button:hover {
  background: #e5e7eb;
  color: #374151;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
}

.input-group:focus-within {
  border-color: #3e3efe;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.input-icon {
  display: flex;
  align-items: center;
  color: #6b7280;
  flex-shrink: 0;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.dialer-input-field {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 0.8125rem;
  color: #111827;
  outline: none;
  padding: 0;
}

.dialer-input-field::placeholder {
  color: #9ca3af;
  font-size: 0.75rem;
}

.contacts-dropdown {
  position: relative;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 8px;
}

.contacts-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 4px;
}

.contact-item {
  padding: 8px 10px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.contact-item:hover {
  background: #f9fafb;
}

.contact-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
}

.contact-name-text {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #111827;
}

.contact-company {
  font-size: 0.6875rem;
  color: #9ca3af;
}

.contact-phones {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 2px;
}

.contact-phone-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.contact-phone-row:hover {
  background: #ebeaff;
}

.phone-type-label {
  font-size: 0.625rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  min-width: 44px;
}

.phone-number-text {
  font-size: 0.75rem;
  color: #374151;
}

.contact-no-phone {
  font-size: 0.6875rem;
  color: #d1d5db;
  padding-left: 2px;
}

.number-display {
  text-align: center;
  font-size: 1.375rem;
  font-weight: 600;
  color: #111827;
  padding: 8px 0;
  letter-spacing: 1px;
  min-height: 40px;
}

.input-flag {
  font-size: 1.125rem;
  line-height: 1;
  flex-shrink: 0;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  color: #3e3efe;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-bottom: 12px;
}

.keypad-btn {
  width: 100%;
  height: 40px;
  border: 1px solid #e4e4e7;
  background: #ffffff;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 400;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.keypad-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: scale(1.02);
}

.keypad-btn:active {
  transform: scale(0.98);
  background: #e5e7eb;
}

.zero-btn {
  position: relative;
}

.zero-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.zero-number {
  font-size: 0.9375rem;
  font-weight: 400;
  color: #6b7280;
}

.zero-plus {
  font-size: 0.6875rem;
  color: #6b7280;
  margin-top: -4px;
}

.dialer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 10px;
}

.call-action-btn {
  flex: 1;
  height: 40px;
  border: none;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0px 4px 6px -1px rgba(22, 163, 74, 0.3);
}

.call-action-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #15803d 0%, #166534 100%);
  transform: translateY(-2px);
  box-shadow: 0px 6px 12px -2px rgba(22, 163, 74, 0.4);
}

.call-action-btn:active:not(:disabled) {
  transform: translateY(0);
}

.call-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: auto;
}

.backspace-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #e4e4e7;
  background: #ffffff;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.2s ease;
}

.backspace-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.backspace-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Call Screen */
.call-screen {
  padding-top: 8px;
}

.call-header {
  text-align: center;
  margin-bottom: 24px;
}

.call-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-avatar {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  color: #3e3efe;
}

.contact-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.phone-number {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 16px;
}

.call-status {
  min-height: 24px;
  margin-top: 8px;
}

.status-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.status-connecting,
.status-ringing,
.status-reconnecting {
  color: #3e3efe;
}

.status-in-call {
  color: #16a34a;
  font-size: 1rem;
  font-weight: 600;
}

.dots::after {
  content: "...";
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%,
  20% {
    content: ".";
  }
  40% {
    content: "..";
  }
  60%,
  100% {
    content: "...";
  }
}

/* Inline IVR Keypad */
.ivr-inline-keypad {
  text-align: center;
  margin-bottom: 16px;
}

.ivr-inline-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 12px;
}

.ivr-inline-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ivr-inline-close {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.15s;
}

.ivr-inline-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.call-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 24px;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.action-button {
  width: 48px;
  height: 48px;
  border: 1px solid #e4e4e7;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: scale(1.05);
}

.action-button.active {
  background: #ebeaff;
  border-color: #3e3efe;
  color: #3e3efe;
}

.hangup-button {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: white !important;
}

.hangup-button:hover {
  background: #dc2626 !important;
  border-color: #dc2626 !important;
}

.action-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
}

.action-label.active {
  color: #3e3efe;
  font-weight: 600;
}

.microphone-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 180px;
  padding: 4px;
}

.microphone-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.microphone-dropdown li {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.microphone-dropdown li:hover {
  background: #f3f4f6;
  color: #111827;
}

.chevron-icon {
  position: absolute;
  bottom: 4px;
  right: 4px;
  color: #9ca3af;
}

.ivr-input-wrapper {
  margin-bottom: 8px;
}

.ivr-input-wrapper .dialer-input-field {
  padding: 6px 10px;
  background: #f9fafb;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 600;
}

.ivr-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.ivr-input-wrapper .ivr-input-group .dialer-input-field {
  padding-right: 32px;
}

.ivr-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.15s ease;
}

.ivr-clear-btn:hover {
  color: #6b7280;
}

/* AI Assistant Pre-Call Prompt */
.ai-assistant-prompt {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  border-radius: 12px;
  animation: prompt-slide-in 0.2s ease-out;
}

@keyframes prompt-slide-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-prompt-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4338ca;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.ai-prompt-text {
  font-size: 12px;
  color: #4b5563;
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.ai-prompt-subtext {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.ai-prompt-subtext {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.ai-prompt-subtext {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
  font-style: italic;
}

.ai-prompt-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-prompt-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.ai-prompt-btn--primary {
  background: #4f46e5;
  color: white;
}

.ai-prompt-btn--primary:hover {
  background: #4338ca;
}

.ai-prompt-btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.ai-prompt-btn--secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

/* Calling From Section */
.calling-from-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e4e4e7;
}

.calling-from-label {
  font-size: 12px;
  color: #646464;
  margin-bottom: 8px;
  font-weight: 400;
}

.phone-number-select-wrapper {
  display: flex;
  gap: 8px;
  align-items: stretch;
  position: relative;
  width: 100%;
}

.phone-number-select {
  flex: 1;
  min-width: 0;
}

.phone-number-display {
  flex: 1;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  display: flex;
  align-items: center;
  min-width: 0;
}

.add-phone-btn-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.add-option-btn-side {
  background: #3e3efe;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: white;
  flex-shrink: 0;
  align-self: center;
  cursor: pointer;
}

.add-option-btn-side:hover {
  background: #2d2dff;
}

.no-phone-number-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
}

.banner-info-icon {
  font-size: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.banner-text {
  color: #374151;
  flex: 1;
}

.banner-link-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #3e3efe;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;
}

.banner-link-button:hover {
  color: #3230d6;
}

.banner-arrow-icon {
  font-size: 14px;
}

.buy-phone-card {
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 280px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-bottom: 4px;
}

.buy-phone-card .card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.buy-phone-card .header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.buy-phone-card .header-content .icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.buy-phone-card .card-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1;
  margin-bottom: 0px !important;
}

.buy-phone-card .btn-close-card {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.buy-phone-card .btn-close-card:hover {
  background: #e9ecef;
  color: #333;
}

.buy-phone-card .card-body {
  padding: 16px;
}

.buy-phone-card .card-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.4;
}

/* Pre-call AI Assistant prompt */
.ai-assistant-prompt {
  background: #fbfbfe;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #c4b5fd;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fbfbfe;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #c4b5fd;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ai-prompt-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
}

.ai-prompt-text {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
  line-height: 1.4;
}

.ai-prompt-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.ai-prompt-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  transition: all 0.2s;
  flex: 1;
  border: none;
}

.ai-prompt-btn--primary {
  background: #2d60ff;
  color: #fff;
}

.ai-prompt-btn--primary:hover {
  background: #1a4fd6;
  transform: translateY(-1px);
}

.ai-prompt-btn--secondary {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.ai-prompt-btn--secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.ai-prompt-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}

.ai-preparing-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: dialer-ai-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes dialer-ai-spin {
  to {
    transform: rotate(360deg);
  }
}

/* AI Suggestion Bubble */
.ai-suggestion-bubble {
  background: #fbfbfe;
  border: 1px solid #c4b5fd;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.08);
}

.suggestion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.robot-icon-wrapper {
  width: 24px;
  height: 24px;
  background: #ede9fe;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
}

.suggestion-label {
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.suggestion-content {
  min-height: 20px;
}

.suggestion-text {
  font-size: 13.5px;
  line-height: 1.5;
  color: #1f2937;
  margin: 0;
  white-space: pre-wrap;
}

.suggestion-text.muted {
  color: #9ca3af;
  font-style: italic;
}

.typing-cursor {
  color: #7c3aed;
  font-weight: bold;
  animation: blink 0.8s step-end infinite;
  margin-left: 2px;
}

/* Thinking Loader */
.thinking-loader {
  display: flex;
  gap: 3px;
}

.thinking-loader span {
  width: 5px;
  height: 5px;
  background: #7c3aed;
  border-radius: 50%;
  animation: thinking-bounce 1.4s infinite ease-in-out both;
}

.thinking-loader span:nth-child(1) {
  animation-delay: -0.32s;
}
.thinking-loader span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes thinking-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes blink {
  from,
  to {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
